import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button, Input, Select } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { CallButton } from '@/phone/CallButton';
import { dateTime, money } from '@/lib/format';
import { createDealFromLead, dismissLead, getLead, linkLeadToDeal, restoreLead, updateLeadSource } from '@/data/client';
import type { LeadDetail } from '@/data/types';
import { FormError } from './Modal';
import { OptionSelect } from './OptionSelect';
import { SourceBadge } from './SourceBadge';

type Mode = 'view' | 'deal' | 'link' | 'dismiss' | 'source';
type Action = Exclude<Mode, 'view'> | 'restore';

/* One lead, its full attribution fingerprint, and the three inbox decisions:
   start a deal, attach it to an existing deal (same person enquiring again), or
   dismiss it (spam, job seeker, wrong number). A manual lead's source can be
   corrected at any time (imported deals all start as "Cold call"). */
export function LeadDrawer({ leadId, onClose }: { leadId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['lead', leadId], queryFn: () => getLead(leadId) });
  const [mode, setMode] = useState<Mode>('view');
  const [dealName, setDealName] = useState('');
  const [mrr, setMrr] = useState('');
  const [dealId, setDealId] = useState('');
  const [reason, setReason] = useState('');
  const [sourceId, setSourceId] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useEffect(() => { setMode('view'); }, [leadId]);

  const done = () => {
    for (const key of ['leads', 'leadSummary', 'lead', 'deals', 'deal', 'companies', 'company']) void qc.invalidateQueries({ queryKey: [key] });
    setMode('view');
  };
  const act = useMutation({
    mutationFn: async (m: Action) => {
      if (m === 'deal') return createDealFromLead(leadId, { name: dealName || undefined, mrr: mrr || undefined });
      if (m === 'link') return linkLeadToDeal(leadId, dealId);
      if (m === 'dismiss') return dismissLead(leadId, reason);
      if (m === 'source') return updateLeadSource(leadId, sourceId);
      return restoreLead(leadId);
    },
    onSuccess: done,
  });

  const lead = data?.lead;
  return (
    <div className="lead-drawer">
      <div className="lead-drawer__scrim" onClick={onClose} />
      <aside className="lead-drawer__panel" aria-label="Lead">
        <div className="lead-drawer__top">
          <div className="lead-drawer__id">
            <div>
              <strong>{lead?.name ?? '…'}</strong>
              {lead && <span className="crm-cell-sub">{lead.origin === 'manual' ? 'Added manually' : 'From leadIQ'} · {dateTime(lead.receivedAt)}</span>}
            </div>
          </div>
          <button type="button" className="lead-modal__x" onClick={onClose} aria-label="Close"><Icon name="x" size={20} /></button>
        </div>

        {lead && (
          <>
            <div className="lead-drawer__contact">
              <Link to={`/companies/${lead.companyId}`}><Icon name="layers" size={14} /> {lead.companyName}</Link>
              {lead.email && <a href={`mailto:${lead.email}`}><Icon name="mail" size={14} /> {lead.email}</a>}
              {lead.phone && <CallButton to={lead.phone} name={lead.companyName ?? lead.phone} />}
            </div>
            <div className="lead-drawer__meta">
              <div><em>Source</em><SourceBadge lead={lead} /></div>
              <div><em>Status</em><span>{statusText(lead)}</span></div>
              <div><em>Channel</em><span>{lead.channel ?? '—'}</span></div>
              <div><em>leadIQ score</em><span>{lead.aiQualityScore != null ? `${lead.aiQualityScore} / 5` : '—'}</span></div>
            </div>
            <div className="lead-drawer__content">
              {lead.message && <p className="crm-lead-msg">{lead.message}</p>}
              <Attribution lead={lead} />
            </div>
            <div className="lead-drawer__foot crm-drawer-foot">
              {mode === 'view' && <Actions lead={lead} hasDeals={!!data?.openDeals.length} onMode={(m) => { if (m === 'source') setSourceId(lead.sourceOptionId ?? ''); setMode(m); }} onRestore={() => act.mutate('restore')} />}
              {mode === 'deal' && (
                <div className="crm-form">
                  <div className="crm-grid2">
                    <Input label="Deal name" placeholder={lead.companyName} value={dealName} onChange={(e) => setDealName(e.target.value)} />
                    <Input label="Expected MRR" inputMode="decimal" placeholder="$" value={mrr} onChange={(e) => setMrr(e.target.value)} />
                  </div>
                  <Confirm label="Create deal" busy={act.isPending} onConfirm={() => act.mutate('deal')} onCancel={() => setMode('view')} />
                </div>
              )}
              {mode === 'link' && (
                <div className="crm-form">
                  <Select label="Open deal" placeholder="Choose a deal" value={dealId} onChange={(e) => setDealId(e.target.value)}
                    options={(data?.openDeals ?? []).map((d) => ({ value: d.id, label: `${d.name} · ${d.stageName}${d.mrr ? ` · ${money(d.mrr)}/mo` : ''}` }))} />
                  <p className="crm-cell-sub">The deal keeps crediting whichever of its leads arrived first.</p>
                  <Confirm label="Link lead" busy={act.isPending} disabled={!dealId} onConfirm={() => act.mutate('link')} onCancel={() => setMode('view')} />
                </div>
              )}
              {mode === 'source' && (
                <div className="crm-form">
                  <OptionSelect kind="lead_source" label="Source" required value={sourceId} onChange={setSourceId} />
                  <Confirm label="Save source" busy={act.isPending} disabled={!sourceId || sourceId === lead.sourceOptionId} onConfirm={() => act.mutate('source')} onCancel={() => setMode('view')} />
                </div>
              )}
              {mode === 'dismiss' && (
                <div className="crm-form">
                  <Input label="Reason" placeholder="Spam, job seeker, wrong number…" autoFocus value={reason} onChange={(e) => setReason(e.target.value)} />
                  <Confirm label="Dismiss" busy={act.isPending} onConfirm={() => act.mutate('dismiss')} onCancel={() => setMode('view')} />
                </div>
              )}
              <FormError error={act.error} />
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

function statusText(lead: LeadDetail): string {
  if (lead.status === 'linked') return `On deal: ${lead.dealName}`;
  if (lead.status === 'dismissed') return `Dismissed${lead.dismissedReason ? ` — ${lead.dismissedReason}` : ''}`;
  return 'New';
}

function Actions({ lead, hasDeals, onMode, onRestore }: { lead: LeadDetail; hasDeals: boolean; onMode: (m: Mode) => void; onRestore: () => void }) {
  const changeSource = lead.origin === 'manual' && <Button variant="ghost" onClick={() => onMode('source')}>Change source</Button>;
  if (lead.status === 'linked') {
    return <div className="crm-record__actions"><Link to={`/deals/${lead.dealId}`}><Button variant="outline">Open deal: {lead.dealName}</Button></Link>{changeSource}</div>;
  }
  if (lead.status === 'dismissed') return <div className="crm-record__actions"><Button variant="outline" onClick={onRestore}>Restore to inbox</Button>{changeSource}</div>;
  return (
    <div className="crm-record__actions">
      <Button onClick={() => onMode('deal')} iconLeft={<Icon name="plus" size={16} />}>Create deal</Button>
      {hasDeals && <Button variant="outline" onClick={() => onMode('link')}>Link to open deal</Button>}
      {changeSource}
      <Button variant="ghost" onClick={() => onMode('dismiss')}>Dismiss</Button>
    </div>
  );
}

function Confirm({ label, busy, disabled, onConfirm, onCancel }: { label: string; busy: boolean; disabled?: boolean; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="crm-record__actions">
      <Button onClick={onConfirm} disabled={busy || disabled}>{busy ? 'Saving…' : label}</Button>
      <Button variant="ghost" onClick={onCancel}>Cancel</Button>
    </div>
  );
}

/* The snapshot copied at intake. Shown in full because it is what revenue will be
   credited to — a rep should be able to see why a lead counts as "Paid". */
function Attribution({ lead }: { lead: LeadDetail }) {
  const a = lead.attribution;
  const rows: [string, string | null][] = [
    ['Campaign', a.utmCampaign], ['Campaign key', a.campaignKey], ['UTM source', a.utmSource], ['UTM medium', a.utmMedium],
    ['Keyword', a.utmTerm], ['Ad content', a.utmContent], ['Google click id', a.gclid], ['Meta click id', a.fbclid],
    ['Referrer', a.referrer], ['Landing page', a.referringPage], ['Stated source', a.sourceChannel],
    ['leadIQ id', a.sourceUid],
  ];
  const shown = rows.filter(([, v]) => v);
  if (!shown.length) return null;
  return (
    <div className="crm-attr">
      <h4>Attribution</h4>
      <dl className="crm-fields">
        {shown.map(([k, v]) => <div key={k} style={{ display: 'contents' }}><dt>{k}</dt><dd>{v}</dd></div>)}
      </dl>
    </div>
  );
}
